import React, { useState } from "react";
import { Search, Edit, Trash2, Plus, X, Save, Loader2, AlertTriangle, Swords } from "lucide-react";
import { Concurrent } from "../../../../types/features/administration/Concurrent";
import useConcurrents from "../../../../hooks/administration/concurrents/useConcurrents";
import DeleteConfirmationModal from "../../../../components/ui/DeleteConfirmationModal";
import { EditConcurrentModal } from "../../../../components/features/administration/concurrents/EditConcurrentModal";

interface FilterState {
  search: string;
}





// Main ConcurrentsPage Component
const ConcurrentsPage: React.FC = () => {
  const { concurrents, deleteConcurrent, updateConcurrent, createConcurrent, concurrentsLoading }
    = useConcurrents({});

  const [filters, setFilters] = useState<FilterState>({ search: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [editingConcurrent, setEditingConcurrent] = useState<Concurrent | null>(null);
  const [deletingConcurrent, setDeletingConcurrent] = useState<Concurrent | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const itemsPerPage = 10;

  // Filter logic
  const filteredConcurrents = (concurrents || []).filter((c) => {
    return (
      filters.search === "" ||
      c.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      c.taxe.toLowerCase().includes(filters.search.toLowerCase())
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredConcurrents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredConcurrents.length);
  const currentConcurrents = filteredConcurrents.slice(startIndex, endIndex);

  const handleAction = (action: string, concurrent: Concurrent) => {
    switch (action) {
      case "edit":
        setIsCreating(false);
        setEditingConcurrent(concurrent);
        break;
      case "delete":
        setDeletingConcurrent(concurrent);
        break;
    }
  };

  const handleSave = async (concurrent: Concurrent) => {
    setIsEditLoading(true);
    try {
      if (isCreating) {
        await createConcurrent(concurrent);
      } else {
        await updateConcurrent(concurrent);
      }
      setEditingConcurrent(null);
      setIsCreating(false);
    } catch (error) {
      console.error("Erreur:", error);
      alert(isCreating ? "Erreur lors de la création" : "Erreur lors de la modification");
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingConcurrent) return;
    setIsDeleteLoading(true);
    try {
      await deleteConcurrent(deletingConcurrent.id);
      setDeletingConcurrent(null);
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      alert("Erreur lors de la suppression du concurrent");
    } finally {
      setIsDeleteLoading(false);
    }
  };

  if (concurrentsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des concurrents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Swords className="w-8 h-8 mt-2 text-green-600" />
            Gestion des Concurrents
          </h1>
          <p className="text-gray-600 mt-1">Gestion des concurrents et de leurs tarifs</p>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Total des concurrents</h3>
            <p className="text-3xl font-bold text-green-600">{concurrents?.length || 0}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Concurrents filtrés</h3>
            <p className="text-3xl font-bold text-blue-600">{filteredConcurrents.length}</p>
          </div>
        </div>
      </div>

      {/* Search + Add */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-center">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par nom, taxe..."
                value={filters.search}
                onChange={(e) => {
                  setFilters({ search: e.target.value });
                  setCurrentPage(1);
                }}
                className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
          <button
            onClick={() => {
              setIsCreating(true);
              setEditingConcurrent({ name: "", taxe: "" });
            }}
            className="ml-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter Concurrent</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Nom</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Taxe</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentConcurrents.length > 0 ? (
                currentConcurrents.map((concurrent) => (
                  <tr key={concurrent.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-800">{concurrent.name}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium">{concurrent.taxe}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleAction("edit", concurrent)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleAction("delete", concurrent)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="px-6 py-8 text-center text-gray-500">
                    Aucun concurrent trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Affichage {startIndex + 1}-{endIndex} sur {filteredConcurrents.length} concurrents
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Précédent
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} sur {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Suivant
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <EditConcurrentModal
        concurrent={editingConcurrent}
        isOpen={!!editingConcurrent}
        onClose={() => {
          setEditingConcurrent(null);
          setIsCreating(false);
        }}
        onSave={handleSave}
        isLoading={isEditLoading}
        isCreating={isCreating}
      />

      <DeleteConfirmationModal
        title={`Êtes-vous sûr de vouloir supprimer le concurrent ${deletingConcurrent?.name}`}
        message="Cette action est irréversible."
        isOpen={!!deletingConcurrent}
        onClose={() => setDeletingConcurrent(null)}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleteLoading}
      />
    </div>
  );
};

export default ConcurrentsPage;
